import { useCallback, useEffect, useMemo, useState } from "react";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import rehypeStringify from "rehype-stringify";
import remarkGfm from "remark-gfm";
import remarkParse from "remark-parse";
import remarkRehype from "remark-rehype";
import { unified } from "unified";
import { createFullDocumentDiff, type DiffResult } from "../utils/diffUtils";
import type { HunkData } from "./unifiedDiffView"; // Import from UnifiedDiffView for now, or move to a type definition file
import {
  type DiffLineEntry,
  getNormalizedLines,
  isAnchorLine,
  normalizeTableBlocks,
  removeQuotes,
  stripAnchors,
  tightenListSpacing,
} from "./utils";

export const useDiffLogic = (
  originalContent: string,
  updatedContent: string,
  onApprovedDocumentChange?: (
    approvedDocument: string,
  ) => Promise<boolean | undefined> | boolean | undefined,
  onClose?: () => void,
) => {
  const [hunkSelections, setHunkSelections] = useState<Map<number, "original" | "updated">>(
    new Map(),
  );
  const [editingHunk, setEditingHunk] = useState<number | null>(null);
  const [editedContent, setEditedContent] = useState<string>("");
  const [hunkData, setHunkData] = useState<Map<number, HunkData>>(new Map());
  const [previewVersion, setPreviewVersion] = useState<"before" | "after">("after");
  const [previousSelection, setPreviousSelection] = useState<"original" | "updated" | null>(null);

  // 差分を計算
  const diffResult: DiffResult = useMemo(() => {
    const processedOriginal = stripAnchors(removeQuotes(originalContent));
    const processedUpdated = stripAnchors(removeQuotes(updatedContent));

    const result = createFullDocumentDiff(processedOriginal, processedUpdated);

    const sanitizedHunks = result.hunks.map((hunk) => ({
      ...hunk,
      lines: hunk.lines.filter((line) => !isAnchorLine(line.content)),
    }));

    return {
      ...result,
      hunks: sanitizedHunks,
    };
  }, [originalContent, updatedContent]);

  // ハンクデータを初期化
  useEffect(() => {
    if (diffResult.hasChanges && hunkData.size === 0) {
      const newHunkData = new Map<number, HunkData>();

      diffResult.hunks.forEach((hunk, index) => {
        const hasChanges = hunk.lines.some((l) => l.type !== "context");
        if (hasChanges) {
          const originalContent = hunk.lines
            .filter((line) => line.type !== "insert")
            .map((line) => line.content)
            .join("\n");

          const updatedContent = hunk.lines
            .filter((line) => line.type !== "delete")
            .map((line) => line.content)
            .join("\n");

          newHunkData.set(index, {
            originalOriginal: originalContent,
            originalUpdated: updatedContent,
            targetOriginal: originalContent,
            targetUpdated: updatedContent,
          });
        }
      });

      setHunkData(newHunkData);

      // 全変更ハンクをデフォルトで承認状態にする
      const defaultSelections = new Map<number, "original" | "updated">();
      newHunkData.forEach((_, key) => {
        defaultSelections.set(key, "updated");
      });
      setHunkSelections(defaultSelections);
    }
  }, [diffResult, hunkData.size]);

  const hunkDisplayOrder = useMemo(() => {
    const map = new Map<number, number>();
    let counter = 0;

    diffResult.hunks.forEach((hunk, index) => {
      const hasChanges = hunk.lines.some((line) => line.type !== "context");
      if (hasChanges) {
        map.set(index, counter++);
      }
    });

    return map;
  }, [diffResult]);

  const buildDocumentText = useCallback(
    (mode: "before" | "after", options?: { includeAnchors?: boolean }): string => {
      const includeAnchors = options?.includeAnchors ?? false;
      const rawEntries: DiffLineEntry[] = [];

      const pushLine = (line: string) => {
        rawEntries.push({ text: line });
      };

      const pushLines = (lines: string[]) => {
        const filtered = includeAnchors ? lines : lines.filter((line) => !isAnchorLine(line));
        filtered.forEach(pushLine);
      };

      diffResult.hunks.forEach((hunk, hunkIndex) => {
        const hasChanges = hunk.lines.some((line) => line.type !== "context");
        const displayIndex = hunkDisplayOrder.get(hunkIndex) ?? hunkIndex;
        const currentHunkData = hunkData.get(hunkIndex);
        const targetOriginal = currentHunkData?.targetOriginal ?? "";
        const targetUpdated = currentHunkData
          ? mode === "after" && editingHunk === hunkIndex
            ? editedContent
            : currentHunkData.targetUpdated
          : "";

        const normalizedDeletes = getNormalizedLines(targetOriginal.split("\n"));
        const normalizedInserts = getNormalizedLines(targetUpdated.split("\n"));

        const isEmptyDiff =
          normalizedDeletes.length > 0 &&
          normalizedDeletes.length === normalizedInserts.length &&
          normalizedDeletes.every((val, idx) => val === normalizedInserts[idx]);

        let startMarkerInserted = false;
        const ensureStartMarker = () => {
          if (!includeAnchors || !hasChanges || startMarkerInserted) return;
          startMarkerInserted = true;
          pushLine("");
          pushLine(`<div data-diff-hunk-start="${displayIndex}" class="diff-hunk-start"></div>`);
          pushLine("");
        };

        const ensureEndMarker = () => {
          if (!includeAnchors || !hasChanges) return;
          pushLine("");
          pushLine(`<div data-diff-hunk-end="${displayIndex}" class="diff-hunk-end"></div>`);
          pushLine("");
        };

        if (!hasChanges) {
          pushLines(hunk.lines.map((line) => line.content));
          return;
        }

        if (mode === "before") {
          ensureStartMarker();
          pushLines(targetOriginal.split("\n"));
          ensureEndMarker();
          return;
        }

        const selection = hunkSelections.get(hunkIndex);

        if (selection === "original") {
          ensureStartMarker();
          pushLines(targetOriginal.split("\n"));
          ensureEndMarker();
          return;
        }

        if (selection === "updated") {
          ensureStartMarker();
          pushLines(targetUpdated.split("\n"));
          ensureEndMarker();
          return;
        }

        if (isEmptyDiff) {
          ensureStartMarker();
          pushLines(targetUpdated.split("\n"));
          ensureEndMarker();
          return;
        }

        // 未選択ハンクは元のコンテンツを維持する（承認していない差分は反映しない）
        ensureStartMarker();
        pushLines(targetOriginal.split("\n"));
        ensureEndMarker();
      });

      if (rawEntries.length === 0) {
        const base = mode === "after" ? updatedContent : originalContent;
        return stripAnchors(removeQuotes(base));
      }

      const compactedEntries = tightenListSpacing(rawEntries);
      return compactedEntries.map((entry) => entry.text).join("\n");
    },
    [
      diffResult,
      hunkSelections,
      hunkData,
      hunkDisplayOrder,
      editingHunk,
      editedContent,
      originalContent,
      updatedContent,
    ],
  );

  const handleSave = useCallback(async () => {
    try {
      const approvedDoc = buildDocumentText("after");

      if (onApprovedDocumentChange) {
        const applied = await onApprovedDocumentChange(approvedDoc);
        if (applied === false) {
          return;
        }
      }

      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error("Apply failed:", error);
    }
  }, [buildDocumentText, onApprovedDocumentChange, onClose]);

  const approvedPreviewDocumentText = useMemo(() => {
    return buildDocumentText("after", { includeAnchors: true });
  }, [buildDocumentText]);

  const originalPreviewDocumentText = useMemo(() => {
    return buildDocumentText("before", { includeAnchors: true });
  }, [buildDocumentText]);

  const getCurrentPreviewSourceText = useCallback(
    () => (previewVersion === "after" ? approvedPreviewDocumentText : originalPreviewDocumentText),
    [previewVersion, approvedPreviewDocumentText, originalPreviewDocumentText],
  );

  const renderMarkdown = useCallback((content: string) => {
    const normalized = normalizeTableBlocks((content || "").replace(/\r\n?/g, "\n"));
    return unified()
      .use(remarkParse)
      .use(remarkGfm)
      .use(remarkRehype, { allowDangerousHtml: true })
      .use(rehypeRaw)
      .use(rehypeSanitize, {
        ...defaultSchema,
        attributes: {
          ...defaultSchema.attributes,
          div: [
            ...(defaultSchema.attributes?.["div"] || []),
            ["className", "diff-hunk-start", "diff-hunk-end"],
            ["dataDiffHunkStart"],
            ["dataDiffHunkEnd"],
          ],
        },
      })
      .use(rehypeStringify, { allowDangerousHtml: true })
      .processSync(normalized)
      .toString();
  }, []);

  const renderedPreviewHtml = useMemo(() => {
    try {
      const previewSourceText = getCurrentPreviewSourceText();
      if (previewSourceText.trim().length === 0) {
        return null;
      }
      return renderMarkdown(previewSourceText);
    } catch (error) {
      console.error("Preview build failed:", error);
      return null;
    }
  }, [getCurrentPreviewSourceText, renderMarkdown]);

  return {
    diffResult,
    hunkData,
    setHunkData,
    hunkSelections,
    setHunkSelections,
    editingHunk,
    setEditingHunk,
    editedContent,
    setEditedContent,
    previewVersion,
    setPreviewVersion,
    hunkDisplayOrder,
    renderedPreviewHtml,
    getCurrentPreviewSourceText,
    handleSave,
    previousSelection,
    setPreviousSelection,
  };
};
